import { useRef, useState } from 'react'
import {
  clockDurationMinutes,
  clockTimeInputError,
  localDate,
  parseClockTime,
  type Project,
  type Task,
  type TimeEntry
} from '@coneno/c-sheet-shared'
import { readStorage } from '@/lib/storage'
import { timesheetPreferenceKeys } from '../lib/preferences'

type EntryDraftOptions = {
  weekStart: string
  weekEnd: string
  /** Used when the draft has no person of its own. */
  defaultPersonId: string
  projects: Project[] | undefined
  allTasks: Task[] | undefined
}

export type EntryRow = { personId: string; projectId: string; taskId: string | null }

/** The entry being added or edited in the popover: its fields, validation and how it was opened. */
export function useEntryDraft({
  weekStart,
  weekEnd,
  defaultPersonId,
  projects,
  allTasks
}: EntryDraftOptions) {
  const [popoverKey, setPopoverKey] = useState<string | null>(null)
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [personId, setPersonId] = useState('')
  const [projectId, setProjectId] = useState(
    () => readStorage(timesheetPreferenceKeys.project) ?? ''
  )
  const [taskId, setTaskId] = useState(() => readStorage(timesheetPreferenceKeys.task) ?? '')
  const [workDate, setWorkDate] = useState(localDate)
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [clockValidationEnabled, setClockValidationEnabled] = useState(false)
  const [description, setDescriptionValue] = useState('')
  const [billable, setBillable] = useState(
    () => (readStorage(timesheetPreferenceKeys.billable) ?? '') !== 'false'
  )
  // Browsing earlier descriptions with the arrow keys; typing starts over.
  const descriptionHistory = useRef<{
    descriptions: string[]
    index: number
    draft: string
  } | null>(null)

  function setDescription(value: string): void {
    descriptionHistory.current = null
    setDescriptionValue(value)
  }

  /** Steps through earlier descriptions; returns false when there is nothing to browse. */
  function browseDescriptions(direction: 'ArrowUp' | 'ArrowDown', descriptions: string[]): boolean {
    if (!descriptionHistory.current) {
      if (!descriptions.length) return false
      descriptionHistory.current = { descriptions, index: -1, draft: description }
    }
    const history = descriptionHistory.current
    history.index =
      direction === 'ArrowUp'
        ? Math.min(history.index + 1, history.descriptions.length - 1)
        : Math.max(history.index - 1, -1)
    setDescriptionValue(history.index === -1 ? history.draft : history.descriptions[history.index])
    return true
  }

  function clearTimes(): void {
    setStartTime('')
    setEndTime('')
    setClockValidationEnabled(false)
    setDescription('')
  }

  function close(): void {
    setEditingId(null)
    setPopoverKey(null)
    setAnchor(null)
  }

  function reset(): void {
    setPersonId('')
    setProjectId(readStorage(timesheetPreferenceKeys.project) ?? '')
    setTaskId(readStorage(timesheetPreferenceKeys.task) ?? '')
    setWorkDate(weekStart <= localDate() && localDate() <= weekEnd ? localDate() : weekStart)
    clearTimes()
    setBillable((readStorage(timesheetPreferenceKeys.billable) ?? '') !== 'false')
    close()
  }

  /** After an entry was added: keep the selections, clear the times and close. */
  function afterCreate(): void {
    clearTimes()
    close()
  }

  /** A new entry from a person's row, starting from the remembered project and task. */
  function prepareNew(target: HTMLElement, date: string, forPersonId: string): void {
    const recentProjectId = readStorage(timesheetPreferenceKeys.project) ?? ''
    const recentTaskId = readStorage(timesheetPreferenceKeys.task) ?? ''
    const recentProject = projects?.find(
      (project) => project.id === recentProjectId && project.active
    )
    const recentTask = allTasks?.find(
      (task) => task.id === recentTaskId && task.projectId === recentProject?.id && task.active
    )
    setEditingId(null)
    setPersonId(forPersonId)
    setWorkDate(date)
    setProjectId(recentProject?.id ?? '')
    setTaskId(recentTask?.id ?? '')
    clearTimes()
    setBillable(recentProject?.billable ?? true)
    setAnchor(target)
    setPopoverKey(`new:${date}`)
  }

  /** A new entry from a project row, so project and task are already chosen. */
  function prepareFromRow(target: HTMLElement, date: string, row: EntryRow): void {
    setEditingId(null)
    setPersonId(row.personId)
    setProjectId(row.projectId)
    setTaskId(row.taskId ?? '')
    setWorkDate(date)
    clearTimes()
    setBillable(projects?.find((project) => project.id === row.projectId)?.billable ?? true)
    setAnchor(target)
    setPopoverKey(`row:${row.projectId}:${date}`)
    requestAnimationFrame(() =>
      document.querySelector<HTMLInputElement>('#entry-start-time')?.focus()
    )
  }

  function edit(target: HTMLElement, entry: TimeEntry): void {
    setEditingId(entry.id)
    setPersonId(entry.personId)
    setProjectId(entry.projectId)
    setTaskId(entry.taskId ?? '')
    setWorkDate(entry.workDate)
    setStartTime(entry.startTime)
    setEndTime(entry.endTime)
    setClockValidationEnabled(false)
    setDescription(entry.description ?? '')
    setBillable(entry.billable)
    setAnchor(target)
    setPopoverKey(`edit:${entry.id}`)
    requestAnimationFrame(() => {
      const input = document.querySelector<HTMLInputElement>('#entry-start-time')
      input?.focus()
      input?.select()
    })
  }

  function selectProject(id: string): void {
    setProjectId(id)
    setTaskId('')
    const project = projects?.find((item) => item.id === id)
    if (project) setBillable(project.billable)
  }

  const durationMinutes = clockDurationMinutes(startTime, endTime)
  const startTimeError = clockTimeInputError(startTime)
  const endTimeError = clockTimeInputError(endTime)
  const timeRangeError =
    parseClockTime(startTime) && parseClockTime(endTime) && !durationMinutes
      ? 'Start time must be earlier than end time.'
      : null
  const clockTimeError = timeRangeError ?? startTimeError ?? endTimeError

  return {
    open: popoverKey !== null,
    anchor,
    editingId,
    // Started from a project row or an existing entry, the project and task are already chosen.
    projectPrefilled: Boolean(editingId) || Boolean(popoverKey?.startsWith('row:')),
    personId,
    effectivePersonId: personId || defaultPersonId,
    projectId,
    taskId,
    workDate,
    startTime,
    endTime,
    description,
    billable,
    durationMinutes,
    startTimeError,
    endTimeError,
    timeRangeError,
    clockValidationEnabled,
    visibleClockTimeError: clockValidationEnabled ? clockTimeError : null,
    setPersonId,
    selectProject,
    setTaskId,
    setWorkDate,
    setStartTime,
    setEndTime,
    setClockValidationEnabled,
    setDescription,
    browseDescriptions,
    setBillable,
    reset,
    afterCreate,
    prepareNew,
    prepareFromRow,
    edit
  }
}

export type EntryDraft = ReturnType<typeof useEntryDraft>
